import { defineStore } from "pinia"
import { ref, watch } from "vue"
import { useRouter, useRoute } from "vue-router"
import { startOfToday, format } from "date-fns"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore.js"
import useFilterStore from "@/stores/filterStore.js"
import { PORTFOLIO } from "@/stores/repower/meteringPointStoreFactories.js"

const PARAM_METERING_POINT = "id"
const PARAM_DATE_RANGE = "date"
const PARAM_MODE = "mode"

export function meteringPointSelectionRoute(currentRoute) {
  if (currentRoute.meta.selectsAsset) {
    return currentRoute.name
  }
  return "assets-analysis"
}

/**
 * Deserialize a date range string to an array of two Date objects.
 */
function deserializeDateRange(rangeString) {
  const parts = rangeString.split("_")
  switch (parts.length) {
    case 1:
      return [new Date(parts[0]), new Date(parts[0])]
    case 2:
      return [new Date(parts[0]), new Date(parts[1])].sort((a, b) => a - b)
    default:
      return [startOfToday(), startOfToday()]
  }
}

/**
 * Serialize an array of two Date objects to a date range string.
 */
function serializeDateRange(dateRange) {
  if (!dateRange || dateRange.length < 2) {
    return ""
  }

  const str1 = format(dateRange[0], "yyyy-MM-dd")
  const str2 = format(dateRange[1], "yyyy-MM-dd")
  if (str1 === str2) {
    return str1
  }
  return `${str1}_${str2}`
}

/**
 * The navigation store is responsible for maintaining continuity of the
 * application state when the user lands on and navigates between routes:
 * - Ensure the initial route is respected when a users needs to log in first
 * - Apply the filter settings from the URL query parameters upon initial load
 * - Ensure query parameters reflect the current filter settings at all times
 * - Navigate to a search page when the user clicks the search button
 */
export default defineStore("navigationStore", () => {
  const meteringPointsStore = useMeteringPointsStore()
  const filterStore = useFilterStore()
  const router = useRouter()
  const route = useRoute()

  const initialRoute = ref()
  const initialMeteringPointId = ref()
  const initialDateRange = ref()
  const initialMode = ref()
  const skipDefaultDateRange = ref(false)
  const doOpenSearch = ref(false)
  const isApplyingMeteringPointQuery = ref(true)

  /**
   * Promise for storing the initial route and query filter settings after the
   * router is ready. Sets isApplyingMeteringPointQuery to false if the
   * route/query combination does not require it.
   */
  const queryPromise = router.isReady().then(() => {
    // Store initial route for redirecting to after login.
    initialRoute.value = route.name
    // Parse and store initial query filter settings.
    if (PARAM_METERING_POINT in route.query) {
      initialMeteringPointId.value = parseInt(route.query[PARAM_METERING_POINT])
    }
    if (PARAM_DATE_RANGE in route.query) {
      initialDateRange.value = deserializeDateRange(
        route.query[PARAM_DATE_RANGE],
      )
    }
    if (PARAM_MODE in route.query) {
      initialMode.value = route.query[PARAM_MODE]
    }
    // Short-circuit metering pointquery applying state if not required.
    if (!route.meta.queryId || !initialMeteringPointId.value) {
      isApplyingMeteringPointQuery.value = false
    }
  })

  /**
   * Apply the metering point selection from the initial query parameters to the
   * filterStore. This only works if both the metering point and its portfolio
   * are available.
   */
  function applyQueryMeteringPoint() {
    const meteringPoint = meteringPointsStore.meteringPoints.find(
      (mp) => mp.id === initialMeteringPointId.value,
    )
    if (meteringPoint) {
      // Metering point is a portfolio; set both the current metering point and
      // the current portfolio to the portfolio from the query.
      if (meteringPoint.role === PORTFOLIO) {
        filterStore.setMeteringPoint(meteringPoint)
        filterStore.setPortfolio(meteringPoint, true)
        return
      }
      // Metering point is an asset or allocation point
      const portfolio = meteringPointsStore.portfolios.find(
        (p) => p.id === meteringPoint.portfolioId,
      )
      if (portfolio) {
        filterStore.setMeteringPoint(meteringPoint)
        filterStore.setPortfolio(portfolio, true)
      }
    }
  }

  /**
   * Apply the filter settings from the initial query parameters to the
   * filterStore.
   */
  function applyQuery() {
    let promise
    if (initialMeteringPointId.value) {
      // Wait for the metering points to be loaded too.
      promise = Promise.all([
        meteringPointsStore.overviewPromise,
        queryPromise,
      ]).then(applyQueryMeteringPoint)
    } else {
      promise = queryPromise
    }
    promise
      .then(() => {
        if (initialDateRange.value) {
          filterStore.setDateRange(initialDateRange.value)
          skipDefaultDateRange.value = true
        }
        if (initialMode.value) {
          filterStore.setMode(initialMode.value)
        }
      })
      .finally(() => {
        isApplyingMeteringPointQuery.value = false
      })
  }

  /**
   * Navigate to the route the user initially navigated to before being
   * redirected to the login page.
   */
  function goToInitialRoute() {
    let destination
    switch (initialRoute.value) {
      case null:
      case "login":
        destination = "dashboard"
        break
      default:
        destination = initialRoute.value
    }
    router.push({ name: destination }).then(() => {})
  }

  /**
   * Navigate to a search page when the user clicks the search button and have
   * it open the search menu.
   */
  function openSearch() {
    doOpenSearch.value = true
    return router.push({ name: meteringPointSelectionRoute(route) })
  }

  /**
   * Make a query object from the current metering point, date range, and mode
   * based on the route and filter settings.
   */
  function makeQuery(currentRoute, meteringPoint, dateRange, mode) {
    const query = { ...currentRoute.query }

    if (currentRoute.meta.queryId) {
      query[PARAM_METERING_POINT] = meteringPoint?.id
    } else {
      delete query[PARAM_METERING_POINT]
    }

    if (currentRoute.meta.queryDate) {
      query[PARAM_DATE_RANGE] = serializeDateRange(dateRange)
    } else {
      delete query[PARAM_DATE_RANGE]
    }

    if (currentRoute.meta.queryMode) {
      query[PARAM_MODE] = mode
    } else {
      delete query[PARAM_MODE]
    }
    return query
  }

  /**
   * Update the URL query parameters when the filter settings change.
   */
  watch(
    [
      () => filterStore.currentMeteringPoint,
      () => filterStore.dateRange,
      () => filterStore.mode,
      route,
    ],
    ([meteringPoint, dateRange, mode, currentRoute]) => {
      const query = makeQuery(currentRoute, meteringPoint, dateRange, mode)
      // Only replace if query actually changed to avoid double navigation
      const currentQuery = currentRoute.query
      const hasChanged =
        query[PARAM_METERING_POINT] !== currentQuery[PARAM_METERING_POINT] ||
        query[PARAM_DATE_RANGE] !== currentQuery[PARAM_DATE_RANGE] ||
        query[PARAM_MODE] !== currentQuery[PARAM_MODE]
      if (hasChanged) {
        router.replace({ query })
      }
    },
  )

  return {
    // state
    initialRoute,
    initialMeteringPointId,
    initialDateRange,
    initialMode,
    skipDefaultDateRange,
    doOpenSearch,
    isApplyingMeteringPointQuery,
    // actions
    applyQuery,
    goToInitialRoute,
    openSearch,
  }
})
